// src/components/ColaboradorDoMes/GerenciarColaboradorDoMes.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../../firebaseConfig';
import { doc, getDoc, collection, getDocs, updateDoc, serverTimestamp, query, where } from 'firebase/firestore';
import { FaTrophy, FaChartBar, FaCheck, FaArrowLeft, FaMedal, FaLock, FaUsersSlash, FaUsers, FaTimes, FaQuoteLeft } from 'react-icons/fa';
import '../../styles/GerenciarColaboradorDoMes.css';

const MONTHS = [
    'Janeiro','Fevereiro','Março','Abril','Maio','Junho',
    'Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'
];

const CrownModal = ({ winner, onConfirm, onCancel, isClosing }) => {
    const now = new Date();
    const [month, setMonth] = useState(MONTHS[now.getMonth()]);
    const [year, setYear]   = useState(String(now.getFullYear()));
    const [message, setMessage] = useState('');

    const years = Array.from({ length: 5 }, (_, i) => String(now.getFullYear() - 2 + i));

    return (
        <div className="crown-modal-backdrop" onClick={onCancel}>
            <div className="crown-modal" onClick={e => e.stopPropagation()}>
                <button className="crown-modal-close" onClick={onCancel}><FaTimes /></button>

                <div className="crown-modal-header">
                    <div className="crown-modal-trophy"><FaMedal /></div>
                    <h2>Coroar Colaborador</h2>
                    <p>Confirme os detalhes antes de publicar no Mural de Honra.</p>
                </div>

                <div className="crown-modal-winner-preview">
                    <img
                        src={winner.photoURL || 'https://via.placeholder.com/80'}
                        alt={winner.name}
                        className="crown-winner-photo"
                    />
                    <div>
                        <strong>{winner.name}</strong>
                        <span>{winner.departamento || winner.loja}</span>
                        <span className="crown-vote-badge">{winner.votes} votos</span>
                    </div>
                </div>

                <div className="crown-modal-fields">
                    <label>Período de referência</label>
                    <div className="crown-period-row">
                        <select value={month} onChange={e => setMonth(e.target.value)}>
                            {MONTHS.map(m => <option key={m}>{m}</option>)}
                        </select>
                        <select value={year} onChange={e => setYear(e.target.value)}>
                            {years.map(y => <option key={y}>{y}</option>)}
                        </select>
                    </div>

                    <label>
                        Mensagem para o Mural <span className="optional-tag">opcional</span>
                    </label>
                    <textarea
                        placeholder="Ex: Pela dedicação excepcional e pelo espírito de equipe que inspira a todos..."
                        value={message}
                        onChange={e => setMessage(e.target.value)}
                        rows={3}
                        maxLength={280}
                    />
                    <span className="char-counter">{message.length}/280</span>
                </div>

                <div className="crown-modal-actions">
                    <button className="btn-cancel-crown" onClick={onCancel} disabled={isClosing}>
                        Cancelar
                    </button>
                    <button
                        className="btn-confirm-crown"
                        onClick={() => onConfirm({ month, year, message: message.trim() || null })}
                        disabled={isClosing}
                    >
                        {isClosing ? 'Publicando...' : <><FaMedal /> Publicar no Mural</>}
                    </button>
                </div>
            </div>
        </div>
    );
};

const GerenciarColaboradorDoMes = ({ userData }) => {
    const { surveyId } = useParams();
    const navigate = useNavigate();
    const [survey, setSurvey]               = useState(null);
    const [results, setResults]             = useState([]);
    const [pendingVoters, setPendingVoters] = useState([]);
    const [eligibleCount, setEligibleCount] = useState(0);
    const [loading, setLoading]             = useState(true);
    const [isClosing, setIsClosing]         = useState(false);
    const [crownTarget, setCrownTarget]     = useState(null); // winner selecionado p/ modal

    useEffect(() => {
        const fetchResults = async () => {
            if (userData?.departamento !== 'People' && userData?.role !== 'Admin') {
                navigate('/colaborador-do-mes');
                return;
            }
            try {
                const surveyRef  = doc(db, 'Empresas', userData.empresa_id, 'employeeOfMonth', surveyId);
                const surveySnap = await getDoc(surveyRef);
                if (!surveySnap.exists()) { navigate('/colaborador-do-mes'); return; }

                const surveyData = surveySnap.data();
                setSurvey({ ...surveyData, id: surveySnap.id });

                const votesRef  = collection(surveyRef, 'votes');
                const votesSnap = await getDocs(votesRef);
                const voteCount = {};
                votesSnap.forEach(vDoc => {
                    const cid = vDoc.data().candidateId;
                    voteCount[cid] = (voteCount[cid] || 0) + 1;
                });

                const ranked = surveyData.candidates
                    .map(c => ({ ...c, votes: voteCount[c.id] || 0 }))
                    .sort((a, b) => b.votes - a.votes);
                setResults(ranked);

                const qUsers   = query(collection(db, 'users'), where('empresa_id', '==', userData.empresa_id), where('status', '==', 'active'));
                const usersSnap = await getDocs(qUsers);
                const allUsers  = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));

                let eligible = allUsers;
                if (surveyData.voterRule === 'loja' && surveyData.targetStore !== 'Todas')
                    eligible = eligible.filter(u => u.loja === surveyData.targetStore);
                else if (surveyData.voterRule === 'departamento' && surveyData.targetDept !== 'Todos')
                    eligible = eligible.filter(u => u.departamento === surveyData.targetDept);

                setEligibleCount(eligible.length);
                const votedIds = surveyData.votedUsers || [];
                setPendingVoters(eligible.filter(u => !votedIds.includes(u.id)));
            } catch (err) {
                console.error("Erro ao apurar votos:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchResults();
    }, [surveyId, userData, navigate]);

    const handleConfirmCrown = async ({ month, year, message }) => {
        if (!crownTarget) return;
        setIsClosing(true);
        try {
            const surveyRef = doc(db, 'Empresas', userData.empresa_id, 'employeeOfMonth', survey.id);
            await updateDoc(surveyRef, {
                status:       'closed',
                endedAt:      serverTimestamp(),
                winnerId:     crownTarget.id,
                winnerName:   crownTarget.name,
                winnerPhoto:  crownTarget.photoURL || null,
                winnerVotes:  crownTarget.votes,
                month,
                year,
                winnerMessage: message || null,
                scopeValue:   survey.scopeValue || survey.targetStore || survey.targetDept || 'Geral',
            });
            navigate('/colaborador-do-mes');
        } catch (err) {
            console.error("Erro ao encerrar:", err);
            setIsClosing(false);
        }
    };

    if (loading) return <div className="loading-container"><div className="spinner"></div></div>;

    const totalVotes        = survey.votedUsers?.length || 0;
    const progressPercentage = eligibleCount > 0 ? Math.round((totalVotes / eligibleCount) * 100) : 0;

    return (
        <div className="gerenciar-colaborador-container">
            {crownTarget && (
                <CrownModal
                    winner={crownTarget}
                    onConfirm={handleConfirmCrown}
                    onCancel={() => setCrownTarget(null)}
                    isClosing={isClosing}
                />
            )}

            <button className="btn-back-flat" onClick={() => navigate(-1)}><FaArrowLeft /> Voltar</button>

            <header className="page-header-premium">
                <div className="header-titles">
                    <h1><FaChartBar className="icon-gold" /> Apuração em Tempo Real</h1>
                    <p>{survey.title}</p>
                </div>
                {survey.status === 'closed' && (
                    <div className="badge-closed"><FaLock /> Votação Encerrada</div>
                )}
            </header>

            <div className="gerenciar-layout">
                <div className="ranking-section">
                    <h3 className="section-title">Ranking Atual</h3>
                    <div className="ranking-grid">
                        {results.map((candidate, index) => (
                            <div key={candidate.id} className={`ranking-card ${index === 0 && survey.status === 'active' ? 'leader-card' : ''} ${survey.status === 'closed' && survey.winnerId === candidate.id ? 'winner-card' : ''}`}>
                                <div className="ranking-position">#{index + 1}</div>
                                <div className="candidate-info">
                                    <div className="avatar-wrapper">
                                        <img src={candidate.photoURL || 'https://via.placeholder.com/150'} alt={candidate.name} className="candidate-avatar" />
                                        {index === 0 && <div className="crown-icon"><FaTrophy /></div>}
                                    </div>
                                    <div>
                                        <h3>{candidate.name}</h3>
                                        <span>{candidate.departamento || candidate.loja}</span>
                                    </div>
                                </div>
                                <div className="vote-count">
                                    <strong>{candidate.votes}</strong> <span>votos</span>
                                </div>
                                {survey.status === 'active' && index === 0 && (
                                    <button
                                        className="btn-crown-primary"
                                        onClick={() => setCrownTarget(candidate)}
                                        disabled={isClosing}
                                    >
                                        <FaMedal /> Coroar
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                <div className="insights-section">
                    <div className="insight-card progress-card">
                        <h3><FaUsers /> Engajamento</h3>
                        <div className="progress-stats">
                            <div className="stat">
                                <span className="stat-value">{totalVotes}</span>
                                <span className="stat-label">Votaram</span>
                            </div>
                            <div className="stat">
                                <span className="stat-value">{eligibleCount}</span>
                                <span className="stat-label">Elegíveis</span>
                            </div>
                        </div>
                        <div className="progress-bar-bg">
                            <div className="progress-bar-fill" style={{width: `${progressPercentage}%`}}></div>
                        </div>
                        <p className="progress-text">{progressPercentage}% de participação</p>
                    </div>

                    <div className="insight-card pending-card">
                        <h3><FaUsersSlash /> Faltam Votar ({pendingVoters.length})</h3>
                        {pendingVoters.length > 0 ? (
                            <ul className="pending-list">
                                {pendingVoters.map(user => (
                                    <li key={user.id} className="pending-item">
                                        {user.photoURL
                                            ? <img src={user.photoURL} alt={user.name} className="pending-avatar" />
                                            : <div className="pending-avatar-placeholder">{user.name.charAt(0)}</div>
                                        }
                                        <div className="pending-info">
                                            <span className="pending-name">{user.name}</span>
                                            <span className="pending-dept">{user.departamento || user.loja}</span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <div className="all-voted-state">
                                <FaCheck className="success-icon"/>
                                <p>Todos já votaram!</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GerenciarColaboradorDoMes;